import { Controller } from "@hotwired/stimulus"

/**
 * Twitter Widget Loader Controller
 * Loads Twitter widget script when connected to a container with Twitter embeds
 *
 * Usage:
 * <div data-controller="twitter-widget-loader">
 *   <!-- Twitter embeds here -->
 * </div>
 */
export default class extends Controller {
  connect() {
    // Load Twitter widget script immediately
    this.loadTwitterWidget()
  }

  async loadTwitterWidget() {
    // Check if Twitter widgets are already loaded
    if (window.twttr?.widgets) {
      // Widgets already loaded, just process this element
      window.twttr.widgets.load(this.element)
      return
    }

    // Use or create a global promise for widget loading
    if (!window.twitterWidgetPromise) {
      window.twitterWidgetPromise = this.loadTwitterScript()
    }

    try {
      await window.twitterWidgetPromise
      // Process widgets in this specific element
      if (window.twttr?.widgets) {
        window.twttr.widgets.load(this.element)
      }
    } catch (error) {
      console.error("Failed to load Twitter widgets:", error)
    }
  }

  loadTwitterScript() {
    return new Promise((resolve, reject) => {
      // Check if script already exists
      const existingScript = document.getElementById("twitter-widgets-js")
      if (existingScript) {
        // Script tag exists, wait for it to load
        if (window.twttr?.widgets) {
          resolve()
        } else {
          existingScript.addEventListener("load", resolve, { once: true })
          existingScript.addEventListener("error", reject, { once: true })
        }
        return
      }

      // Create and load script
      const script = document.createElement("script")
      script.id = "twitter-widgets-js"
      script.src = "https://platform.twitter.com/widgets.js"
      script.async = true
      script.crossOrigin = "anonymous"

      script.onload = () => {
        resolve()
      }

      script.onerror = () => {
        reject(new Error("Failed to load Twitter widgets script"))
      }

      document.head.appendChild(script)
    })
  }
}
